import { useTranslation } from "react-i18next";
import { TModalsState, setModalsData } from "../../api/store/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { formInitialValues, formValidationSchema } from "./validation";
import Accordian from "../../components/accordian";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldSubmit,
} from "../../components/admin/formFields";
import { FC, useMemo } from "react";
import { VehicleStatusCode } from "../../api/types/VehicleStatusCode";
import { getVehicleEventStatusDisplayName } from "../../utils/vehicle";
import { mapVehicleEventIconWrapped } from "../../assets/svg/vehicle-event-wrapped";
import svgIcons from "../../assets/svg/svgIcons";
import FormActionButtons from "../../components/admin/formActionButtons";
import FTModal from "../../components/common/FTModal";
import { StatusCodeFilter } from "./statusCodeFilter";

type ModalEventFilterProps = {
  statusCodes: VehicleStatusCode[];
  // `null` means the filter hasn't been applied yet, which shows as
  // everything checked. `[]` is a real "nothing selected".
  filteredStatusCodes: StatusCodeFilter;
  handleEventFiltersChange?: (eventFilters: VehicleStatusCode[]) => void;
  statusCodeDescriptions?: Record<number, string>;
};

// Helper function to group status codes by display name
const groupStatusCodesByDisplayName = (
  statusCodes: VehicleStatusCode[],
  statusCodeDescriptions?: Record<number, string>
) => {
  const grouped = statusCodes.reduce(
    (acc, code) => {
      const displayName = 
        statusCodeDescriptions?.[code] ??
        getVehicleEventStatusDisplayName({
          statuscode: code,
        });
      if (!acc[displayName]) {
        acc[displayName] = [];
      }
      acc[displayName].push(code);
      return acc;
    },
    {} as Record<string, VehicleStatusCode[]>
  );

  return Object.entries(grouped).map(([displayName, codes]) => ({
    displayName,
    codes,
    primaryCode: codes[0], // Use first code for icon
  }));
};

const ModalEventFilter: FC<ModalEventFilterProps> = ({
  statusCodes,
  filteredStatusCodes,
  handleEventFiltersChange,
  statusCodeDescriptions,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "vehicleMap.eventFilter",
  });

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showEventFilter: false }));
  };

  // an unapplied filter opens with every event checked
  const selectedStatusCodes = filteredStatusCodes ?? statusCodes;
  const allSelected =
    statusCodes.length > 0 &&
    statusCodes.every((code) => selectedStatusCodes.includes(code));

  const formik = useFormik({
    initialValues: {
      ...formInitialValues,
      all_events: allSelected,
      status_codes: selectedStatusCodes,
    },
    // the modal stays mounted between opens (it renders null while hidden),
    // so without this it would keep showing the selection from the last time
    // it was opened even after the page reset the filter - e.g. on picking a
    // different trip.
    enableReinitialize: true,
    validationSchema: formValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleEventFiltersChange?.(values.status_codes);
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
    setFieldTouched,
  } = formik;
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: tMain("confirm"),
              type: "button",
              variant: "primary",
              onClick: () => {
                handleSubmit();
                hideModal();
              },
            },
            {
              label: tMain("cancel"),
              type: "button",
              variant: "danger-transparent",
              onClick: () => hideModal(),
            },
          ]}
        />
      </>
    );
  }, []);
  if (modalsState.showEventFilter === false) return null;

  return (
    <FormikProvider value={formik}>
      {/* <div className="justify-center items-start flex overflow-x-hidden overflow-y-auto fixed inset-0 z-overlay outline-none focus:outline-none">
        <div
          className="fixed w-full h-screen bg-modal-overlay z-overlay"
          onClick={hideModal}
        ></div>
        <div className="relative my-6 mx-auto max-w-[calc(100vw-4rem)] w-[416px] z-modal"> */}
      <FTModal
        visible={modalsState.showEventFilter}
        heading={t("heading")}
        onHide={hideModal}
        containerStyle="max-w-[calc(100vw-4rem)] w-[416px]"
        dismissible={false}
        position="center"
        extraWrapperClass={userIsImpersonated ? "translate-y-6" : ""}
      >
        <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
          <div className="event-list max-h-[calc(100vh-300px)] overflow-x-hidden overflow-y-auto col-span-6">
            <Accordian
              title="Exception Event"
              openByDefault={true}
              hasDetailsPage={false}
            >
              <div className="col-span-6 space-y-3">
                <AdminFormFieldCheckbox
                  label={t("all_event")}
                  id="all_event"
                  type="checkbox"
                  name="all_event"
                  checked={
                    statusCodes.length > 0 &&
                    statusCodes.every((code) =>
                      values.status_codes.includes(code)
                    )
                  }
                  onChange={(e) => {
                    // unchecking this clears the selection outright: an empty
                    // list now means "pin nothing" rather than "no filter"
                    setFieldValue(
                      "status_codes",
                      e.target.checked ? statusCodes : []
                    );
                    // the field is `all_events`; writing `all_event` left the
                    // real one stale
                    setFieldValue("all_events", e.target.checked);
                  }}
                  onBlur={handleBlur}
                  icon={svgIcons.AllEventIcon}
                />

                <FieldArray
                  name="status_codes"
                  render={(arrayHelpers) => {
                      const groupedEvents = groupStatusCodesByDisplayName(statusCodes, statusCodeDescriptions);

                    return (
                      <div className="col-span-6 space-y-3">
                        {groupedEvents.map((group, i) => (
                          <AdminFormFieldCheckbox
                            label={group.displayName}
                            id={`status_codes_${group.primaryCode}`}
                            type="checkbox"
                            name={`status_codes[${group.primaryCode}]`}
                            checked={group.codes.some((code) =>
                              values.status_codes.includes(code)
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Add all codes in this group
                                group.codes.forEach((code) => {
                                  if (!values.status_codes.includes(code)) {
                                    arrayHelpers.push(code);
                                  }
                                });
                              } else {
                                // Remove all codes in this group
                                const updatedStatusCodes =
                                  values.status_codes.filter(
                                    (code) => !group.codes.includes(code)
                                  );
                                setFieldValue(
                                  "status_codes",
                                  updatedStatusCodes
                                );
                              }

                              // Update all events checkbox after a short delay to ensure state is updated
                              setTimeout(() => {
                                const currentValues =
                                  formik.getFieldProps("status_codes").value;
                                setFieldValue(
                                  "all_events",
                                  currentValues.length === statusCodes.length
                                );
                              }, 0);
                            }}
                            onBlur={handleBlur}
                            IconComponent={
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: mapVehicleEventIconWrapped(
                                    group.primaryCode,
                                    true
                                  ),
                                }}
                              />
                            }
                            key={i}
                          />
                        ))}
                      </div>
                    );
                  }}
                />
              </div>
            </Accordian>

            {false && (
              <Accordian title="Specific Events" hasDetailsPage={false}>
                <div className="col-span-6 space-y-3">
                  <AdminFormFieldCheckbox
                    label={t("idling")}
                    id="idling"
                    type="checkbox"
                    name="idling"
                    checked={values.idling}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <AdminFormFieldCheckbox
                    label={t("speeding")}
                    id="speeding"
                    type="checkbox"
                    name="speeding"
                    checked={values.speeding}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  {/* <AdminFormFieldCheckbox
                          label={t("traffic")}
                          id="traffic"
                          type="checkbox"
                          name="traffic"
                          checked={values.traffic}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />

                        <AdminFormFieldCheckbox
                          label={t("weather")}
                          id="weather"
                          type="checkbox"
                          name="weather"
                          checked={values.weather}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        /> */}
                </div>
              </Accordian>
            )}
          </div>

          {/* <AdminFormFieldSubmit
                    label={tMain("confirm")}
                    type="button"
                    variant="primary"
                    onClick={() => {
                      handleSubmit();
                      hideModal();
                    }}
                  />

                  <AdminFormFieldSubmit
                    label={tMain("cancel")}
                    type="button"
                    variant="danger-transparent"
                    onClick={hideModal}
                  /> */}
          {MemoizedFormActionButtons}
        </form>
      </FTModal>
      {/* </div>
      </div> */}
    </FormikProvider>
  );
};

export default ModalEventFilter;
